import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { PageLoader } from "@/components/bits";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Join from "@/pages/Join";
import Onboarding from "@/pages/Onboarding";
import DashboardLayout from "@/components/DashboardLayout";
import Home from "@/pages/dashboard/Home";
import Agents from "@/pages/dashboard/Agents";
import AgentBuilder from "@/pages/dashboard/AgentBuilder";
import Retailers from "@/pages/dashboard/Retailers";
import Audiences from "@/pages/dashboard/Audiences";
import Campaigns from "@/pages/dashboard/Campaigns";
import CampaignDetail from "@/pages/dashboard/CampaignDetail";
import Conversations from "@/pages/dashboard/Conversations";
import Orders from "@/pages/dashboard/Orders";
import Workflows from "@/pages/dashboard/Workflows";
import Insights from "@/pages/dashboard/Insights";
import Team from "@/pages/dashboard/Team";
import Platform from "@/pages/dashboard/Platform";
import "@/App.css";

function Protected({ children, requireCompany = true }) {
  const { user, company, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/" replace state={{ from: location }} />;
  if (requireCompany && !company) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to="/dashboard" replace />;
  return children;
}

function ChangePasswordGate() {
  const { user, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user?.must_change_password) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await changePassword(currentPassword, nextPassword);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not change password");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center px-4" data-testid="change-password-gate">
      <form onSubmit={submit} className="glass w-full max-w-md p-6 space-y-4">
        <p className="overline-label">Security</p>
        <h2 className="font-display text-xl font-bold text-slate-50">Set a new password</h2>
        <p className="text-sm text-slate-400">An AgriVoice admin issued a temporary password. Choose a new one to continue.</p>
        <div>
          <label className="field-label" htmlFor="temp-current">Temporary password</label>
          <input id="temp-current" type="password" autoComplete="current-password" required className="field" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="change-password-current" />
        </div>
        <div>
          <label className="field-label" htmlFor="temp-new">New password</label>
          <input id="temp-new" type="password" autoComplete="new-password" required minLength={8} className="field" value={nextPassword} onChange={(e) => setNextPassword(e.target.value)} data-testid="change-password-new" />
        </div>
        {error && <p className="text-xs text-rose-300" data-testid="change-password-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="change-password-submit">{busy ? "Saving…" : "Save new password"}</button>
      </form>
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/join" element={<Join />} />
      <Route path="/onboarding" element={<Protected requireCompany={false}><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
        <Route index element={<Home />} />
        <Route path="agents" element={<Agents />} />
        <Route path="agents/new" element={<AgentBuilder />} />
        <Route path="agents/:id" element={<AgentBuilder />} />
        <Route path="retailers" element={<Retailers />} />
        <Route path="audiences" element={<Audiences />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="campaigns/:id" element={<CampaignDetail />} />
        <Route path="conversations" element={<Conversations />} />
        <Route path="conversations/:id" element={<Conversations />} />
        <Route path="orders" element={<Orders />} />
        <Route path="workflows" element={<Workflows />} />
        <Route path="insights" element={<Insights />} />
        <Route path="team" element={<Team />} />
        <Route path="platform" element={<Platform />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="bottom-right" richColors />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <ChangePasswordGate />
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
